import {
  Typography,
  Switch,
  Checkbox,
  TextField,
  FormControlLabel,
  FormLabel,
  Box,
  FormControl,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import React from 'react';
import { LocaleText } from '../../../locale';
import { EFooterAlignment } from '../types';
import { styles } from '../header/ExportPdfHeaderControllers.styles';

const ALIGNMENT_OPTIONS: { alignment: EFooterAlignment; Icon: React.ElementType; label: string }[] = [
  { alignment: EFooterAlignment.left, Icon: AlignHorizontalLeftIcon, label: 'left' },
  { alignment: EFooterAlignment.center, Icon: AlignHorizontalCenterIcon, label: 'center' },
  { alignment: EFooterAlignment.right, Icon: AlignHorizontalRightIcon, label: 'right' }
];

export interface IExportPdfFooterControllers {
  localeText: LocaleText;
  toggleShowFooter(): void;
  setNote(payload: string): void;
  toggleShowNote(): void;
  toggleShowPageNumber(): void;
  toggleShowExportTimeStamp(): void;
  showFooter: boolean;
  note: string;
  showNote: boolean;
  showPageNumber: boolean;
  showExportTimeStamp: boolean;
  pageNumberAlignment: EFooterAlignment;
  setPageNumberAlignment(payload: EFooterAlignment): void;
  noteAlignment: EFooterAlignment;
  setNoteAlignment(payload: EFooterAlignment): void;
  timestampAlignment: EFooterAlignment;
  setTimestampAlignment(payload: EFooterAlignment): void;
}

const AlignmentSelector: React.FC<{
  value: EFooterAlignment;
  onChange(value: EFooterAlignment): void;
  dataTestPrefix: string;
}> = ({ value, onChange, dataTestPrefix }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_e, newValue) => {
      if (newValue !== null) {
        onChange(newValue as EFooterAlignment);
      }
    }}
    size="medium"
    data-test={`${dataTestPrefix}-alignment`}
    sx={{ marginLeft: 'auto' }}
  >
    {ALIGNMENT_OPTIONS.map(({ alignment, Icon, label }) => (
      <ToggleButton
        key={alignment}
        value={alignment}
        data-test={`${dataTestPrefix}-alignment-${label}`}
        aria-label={label}
      >
        <Icon fontSize="inherit" />
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export const ExportPdfFooterControllers: React.FC<IExportPdfFooterControllers> = ({
  localeText,
  toggleShowFooter,
  setNote,
  toggleShowNote,
  toggleShowPageNumber,
  toggleShowExportTimeStamp,
  showFooter,
  note,
  showNote,
  showPageNumber,
  showExportTimeStamp,
  pageNumberAlignment,
  setPageNumberAlignment,
  noteAlignment,
  setNoteAlignment,
  timestampAlignment,
  setTimestampAlignment
}) => {
  const { showPageNumberLabel, showExportTimestampLabel, noteLabel, footerTitle } =
    localeText.exportPdfModal.footerSection;
  const maxNoteLength = 100;

  return (
    <FormControl fullWidth sx={styles.formControl}>
      <FormLabel id="footer-title-label" sx={styles.formLabel}>
        <Typography variant="h4" component="label" sx={styles.typographyBase}>
          {footerTitle}
        </Typography>
        <Switch
          size="small"
          aria-labelledby="footer-title-label"
          data-test="toggle-footer-options"
          onChange={toggleShowFooter}
          checked={showFooter}
        />
      </FormLabel>

      {showFooter && (
        <Box sx={styles.footerFormContent}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              sx={styles.formControlCheckbox}
              control={
                <Checkbox
                  size="medium"
                  checked={showPageNumber}
                  onChange={toggleShowPageNumber}
                  data-test="footer-option-show-page-number"
                  sx={styles.formControlCheckbox}
                />
              }
              label={
                <Typography variant="bodyDefault" sx={styles.typographyBase}>
                  {showPageNumberLabel}
                </Typography>
              }
            />
            {showPageNumber && (
              <AlignmentSelector
                value={pageNumberAlignment}
                onChange={setPageNumberAlignment}
                dataTestPrefix="page-number"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
            <FormControlLabel
              sx={styles.formControlCheckbox}
              control={
                <Checkbox
                  size="medium"
                  checked={showExportTimeStamp}
                  onChange={toggleShowExportTimeStamp}
                  data-test="footer-option-show-export-timestamp"
                  sx={styles.formControlCheckbox}
                />
              }
              label={
                <Typography variant="bodyDefault" sx={styles.typographyBase}>
                  {showExportTimestampLabel}
                </Typography>
              }
            />
            {showExportTimeStamp && (
              <AlignmentSelector
                value={timestampAlignment}
                onChange={setTimestampAlignment}
                dataTestPrefix="timestamp"
              />
            )}
          </Box>
          <FormControlLabel
            sx={{ ...styles.textFieldControlLabel, marginTop: '8px', marginBottom: '8px' }}
            control={
              <TextField
                size="medium"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                disabled={!showNote}
                multiline
                fullWidth
                inputProps={{ maxLength: maxNoteLength }}
                data-test="footer-option-note"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    marginTop: '8px',
                    width: '100%'
                  }
                }}
              />
            }
            label={
              <Box className="cds-textFieldLabelWithCounter">
                <Checkbox
                  size="medium"
                  checked={showNote}
                  onChange={toggleShowNote}
                  data-test="footer-option-show-note"
                  sx={styles.formControlCheckbox}
                />
                <Typography variant="h4" component="span" sx={styles.typographyBase}>
                  {noteLabel}
                </Typography>
                <Typography variant="bodyDefault" sx={styles.typographyCounter}>
                  {note.length}/{maxNoteLength}
                </Typography>
                {showNote && (
                  <AlignmentSelector value={noteAlignment} onChange={setNoteAlignment} dataTestPrefix="note" />
                )}
              </Box>
            }
            labelPlacement="top"
          />
        </Box>
      )}
    </FormControl>
  );
};
